'use client';

import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { csvToTsvContent } from '../../data/csv-to-tsv-content';
import Link from 'next/link';

const sampleCsv = `name,age,city,isActive,skills
"John Doe",30,New York,true,"JavaScript, TypeScript, React"
"Jane Smith",25,San Francisco,false,"Python, Data Analysis"
"Bob Johnson",35,Chicago,true,"Java, Spring, SQL"`;

const sampleTsv = `name	age	city	isActive	skills
John Doe	30	New York	true	"JavaScript, TypeScript, React"
Jane Smith	25	San Francisco	false	"Python, Data Analysis"
Bob Johnson	35	Chicago	true	"Java, Spring, SQL"`;

function convertCsvToTsv(csvString: string, inputDelimiter = ','): string {
    try {
        if (!csvString.trim()) {
            return '';
        }

        // Parse CSV to JSON using PapaParse
        const results = Papa.parse(csvString, {
            delimiter: inputDelimiter,
            skipEmptyLines: true,
        });

        if (results.errors.length > 0) {
            throw new Error(results.errors[0].message);
        }

        // Convert back to TSV
        return Papa.unparse(results.data, {
            delimiter: '\t',
            quotes: true,
            skipEmptyLines: true,
        });
    } catch (error) {
        throw new Error(`Error converting CSV to TSV: ${error instanceof Error ? error.message : 'Invalid CSV'}`);
    }
}

export default function CsvToTsvTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [inputDelimiter, setInputDelimiter] = useState(',');
    const [isConverting, setIsConverting] = useState(false);
    const { toast } = useToast();

    const convertCsv = useCallback((csvString: string) => {
        try {
            if (!csvString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);
            const tsvString = convertCsvToTsv(csvString, inputDelimiter);
            setOutput(tsvString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid CSV'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, [inputDelimiter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertCsv(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, inputDelimiter, convertCsv]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The TSV has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/tab-separated-values;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.tsv') : 'converted_data.tsv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleCsv);
        setFileName('sample.csv');
    };

    const handleInputDelimiterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setInputDelimiter(e.target.value);
    };

    const swapInputOutput = () => {
        if (output) {
            setInput(output);
            setOutput('');
            setFileName(prev => prev ? prev.replace(/\.(csv|tsv)$/, ext =>
                ext === '.csv' ? '.tsv' : '.csv'
            ) : null);
            setInputDelimiter(prev => prev === '\t' ? ',' : '\t');
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <ToolPageIntro title="CSV to TSV Converter">
                Convert comma-separated values (CSV) to tab-separated values (TSV) instantly. Ideal for developers moving data between systems, analysts preparing datasets for tools that expect TSV, and anyone who needs to avoid comma conflicts in text fields. Paste or upload your CSV; the converter preserves structure and handles quoted fields.
            </ToolPageIntro>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="flex-1 sm:flex-none">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Input Delimiter
                            </label>
                            <select
                                value={inputDelimiter}
                                onChange={handleInputDelimiterChange}
                                className="w-full sm:w-32 bg-background border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 focus:outline-none"
                                disabled={isConverting}
                            >
                                <option value=",">, (Comma)</option>
                                <option value="\t">\t (Tab)</option>
                                <option value=";">; (Semicolon)</option>
                                <option value="|">| (Pipe)</option>
                            </select>
                        </div>

                        <div className="relative flex-1">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Upload File
                            </label>
                            <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                                <Upload className="h-4 w-4 mr-2" />
                                <span className="text-sm truncate max-w-[200px]">
                                    {fileName || 'Choose a file...'}
                                </span>
                                <input
                                    type="file"
                                    accept=".csv,.tsv,text/csv,text/tab-separated-values"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    disabled={isConverting}
                                />
                            </label>
                            {fileName && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileName(null);
                                        setInput('');
                                    }}
                                    className="absolute right-2 top-8 p-1 text-muted-foreground hover:text-foreground"
                                    title="Remove file"
                                    disabled={isConverting}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your data here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    {inputDelimiter === '\t' ? 'TSV' : 'CSV'}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your data here...\n' +
                                        'name,age,city,isActive,skills\n' +
                                        '\"John Doe\",30,New York,true,\"JavaScript, TypeScript\"\n' +
                                        '\"Jane Smith\",25,San Francisco,false,Python'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full h-12 w-12"
                        onClick={swapInputOutput}
                        disabled={!output || isConverting}
                        title="Swap input and output"
                    >
                        <ArrowRight className="h-5 w-5" />
                        <ArrowLeft className="h-5 w-5 absolute" />
                    </Button>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, inputDelimiter === '\t' ? '.csv' : '.tsv') :
                                            'Your converted data will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your converted data will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted data will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="mt-12">
                <InformationalContent
                    title={csvToTsvContent.title}
                    description={csvToTsvContent.description}
                    content={csvToTsvContent.content}
                    faqs={csvToTsvContent.faqs}
                    relatedTools={csvToTsvContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
